import React, { useState, useEffect, useCallback, useRef, Fragment, startTransition } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { MAX_TASK_STEP_COUNT, MAX_NUM_TASK_LINKS, CROSS_BASELINE_SEARCH_URL_PARAMS } from '../../constants/constants';
import { RouteWithParams } from '../../helpers/routing';
import Routes from '../../constants/routes';
import { TruncateText } from '../../helpers/strings';
import { isValidNumber } from '../../helpers/validation';
import InputText from '../Common/InputText';
import TextArea from '../Common/TextArea';
import Toggle from '../Common/Toggle/Toggle';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import Icon from '../Common/Icon';
import DatePicker from '../Common/DatePicker/DatePicker';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import WorkflowSelector from '../BaselineTaskList/WorkflowSelector/WorkflowSelector';
import './BaselineTaskForm.scss';
import {
	BaselineTaskFormProps,
	FormState,
	DropResult,
	TaskStep,
	TaskLink,
} from '../../apis/baselineTaskForm/baselineTaskForm.types';
import BaselineTaskDrawer from './BaselineTaskDrawer';
import isEqual from '../../utils/isEqual';

// Drag and drop styling functions
const getItemStyle = (isDragging: boolean, draggableStyle?: any) => ({
	userSelect: 'none' as const,
	lineHeight: 1,
	margin: '0',
	background: 'white',
	border: isDragging ? '2px solid #03a9f4' : '',
	...draggableStyle,
});

const getListStyle = (isDraggingOver: boolean) => ({
	background: isDraggingOver ? '#eee' : 'white',
	borderRadius: '0.25rem',
	padding: 8,
});

// Error Fallback Component
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({
	error,
	resetErrorBoundary
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong with the task form:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

// Main Content Component
const BaselineTaskFormContent: React.FC<BaselineTaskFormProps> = ({
	task,
	isFormValid,
	showDateError,
	onFormChange,
	onContingentChange,
	onContingentRemove,
	onRemovePrereq,
	onPrereqChange,
	formFilters,
	taskSearch,
	taskPrereqSearch,
	onSave,
	taskSearchResults,
	taskPrereqSearchResults,
	subscriptionId,
	isMobile,
}) => {
	// Component state using hooks
	const [state, setState] = useState<FormState>({
		isRelationshipDrawerOpen: false,
		relationshipBaselineTaskId: null,
		showMore: false,
		markAsDependent: task.contingentId ? true : false,
		linkAsPrereq: task.prereqTasks && task.prereqTasks.length > 0 ? true : false,
		taskSearchTerm: '',
		targetWeeks: 0,
		targetDays: 0,
		startWeeks: 0,
		startDays: null,
		eventId: null,
		selectedWorkflows: task.workflows || [],
		showTargetError: false,
		triedSubmit: false,
	});

	const formRef = useRef<HTMLFormElement>(null);

	// Field validation utility
	const setFieldValidity = useCallback((name: string, message: string = '') => {
		const el = formRef.current?.querySelector(`input[name="${name}"]`) as HTMLInputElement;
		if (el) el.setCustomValidity(message);
	}, []);

	// Validate target fields
	const validateTargetFields = useCallback(() => {
		const { targetWeeks, targetDays } = task;

		const hasWeeks = isValidNumber(targetWeeks);
		const hasDays = isValidNumber(targetDays);

		if (hasWeeks || hasDays) {
			setFieldValidity('targetWeeks', '');
			setFieldValidity('targetDays', '');
			return true;
		} else {
			const msg = 'Please fill in at least Weeks or Days.';
			setFieldValidity('targetWeeks', msg);
			setFieldValidity('targetDays', msg);
			return false;
		}
	}, [task, setFieldValidity]);

	// Form change handlers
	const onFormChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		onFormChange(e.target.name, e.target.value);

		if (e.target.name === 'targetWeeks' || e.target.name === 'targetDays') {
			validateTargetFields();
		}
	}, [onFormChange, validateTargetFields]);

	const onToggleChange = useCallback((name: string, value: boolean) => {
		onFormChange(name, value);
	}, [onFormChange]);

	const onDropdownChange = useCallback((id: number | string, name: string) => {
		startTransition(() => {
			onFormChange(name, id);
		});
	}, [onFormChange]);

	// Successor handling
	const onSuccessorAdd = useCallback((id: number) => {
		const contingentName = taskSearchResults.find((m) => m.id === id)?.name || '';
		startTransition(() => {
			onContingentChange(id, contingentName);
			setState((prev) => ({ ...prev, markAsDependent: false }));
		});
	}, [taskSearchResults, onContingentChange]);

	const onSuccessorRemove = useCallback(() => {
		onContingentRemove();
	}, [onContingentRemove]);

	// Search handlers
	const onTaskSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.keyCode === 13) {
			e.preventDefault();
			const target = e.target as HTMLInputElement;
			const value = target.value;
			if (value && value.length >= 3) {
				taskSearch(value);
			}
		}
	}, [taskSearch]);

	const onPrereqSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		const key = e.key || e.keyCode;
		if (key === 'Enter' || key === 13) {
			e.preventDefault();
			const target = e.target as HTMLInputElement;
			const value = target.value || target.defaultValue;
			if (value?.length >= 3) {
				taskPrereqSearch(value);
			}
		}
	}, [taskPrereqSearch]);

	const onDatePickerChange = useCallback((date: string, name: string) => {
		onFormChange(name, date);
	}, [onFormChange]);

	const onMakeMilestone = useCallback(() => {
		onFormChange('isMilestone', !task.isMilestone);
	}, [onFormChange, task.isMilestone]);

	const showMore = useCallback(() => {
		setState((prev) => ({ ...prev, showMore: !prev.showMore }));
	}, []);

	// Link handlers
	const addLink = useCallback(() => {
		let links = task.links.slice();
		if (links.length < MAX_NUM_TASK_LINKS) {
			links.push({
				id: 0,
				name: '',
				url: '',
				isActive: true,
				ordinal: links.length,
				isFile: false,
			});
			onFormChange('links', links);
		}
	}, [task.links, onFormChange]);

	const addFileLink = useCallback(() => {
		let links = task.links.slice();
		if (links.length < MAX_NUM_TASK_LINKS) {
			links.push({
				id: 0,
				name: '',
				url: '',
				isActive: true,
				ordinal: links.length,
				isFile: true,
			});
			onFormChange('links', links);
		}
	}, [task.links, onFormChange]);

	// Dependency handlers
	const onMarkAsDependent = useCallback(() => {
		setState((prev) => ({ ...prev, markAsDependent: !prev.markAsDependent }));
	}, []);

	const onLinkAsPrereq = useCallback(() => {
		setState((prev) => ({ ...prev, linkAsPrereq: !prev.linkAsPrereq }));
	}, []);

	const onAddStaticDates = useCallback(() => {
		onFormChange('showStaticDates', !task.showStaticDates);
	}, [onFormChange, task.showStaticDates]);

	// Relationship drawer handlers
	const showRelationshipDrawer = useCallback((baselineTaskId: number) => {
		setState((prev) => ({
			...prev,
			relationshipBaselineTaskId: baselineTaskId,
			isRelationshipDrawerOpen: true,
		}));
	}, []);

	const onRelationshipDrawerComplete = useCallback(() => {
		setState((prev) => ({
			...prev,
			relationshipBaselineTaskId: null,
			isRelationshipDrawerOpen: false,
		}));
	}, []);

	// Step handlers
	const onAddStep = useCallback(() => {
		const steps = [...task.steps];
		const nextOrdinal = steps.length > 0 ? steps[steps.length - 1].ordinal + 1 : 1;

		const newStep: TaskStep = {
			id: 0,
			name: '',
			isActive: false,
			ordinal: nextOrdinal,
			baselineTaskId: task.id,
		};

		steps.push(newStep);
		onFormChange('steps', steps);
	}, [task.steps, task.id, onFormChange]);

	const onRemoveStep = useCallback((step: TaskStep) => {
		const steps = task.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		steps.splice(index, 1);

		for (let i = index; i < steps.length; i++) {
			steps[i].ordinal = i + 1;
		}
		onFormChange('steps', steps);
	}, [task.steps, onFormChange]);

	const onUpdateStepName = useCallback((step: TaskStep, e: React.ChangeEvent<HTMLInputElement>) => {
		let steps = task.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		const updatedStep = { ...step, name: e.target.value };
		steps.splice(index, 1, updatedStep);
		onFormChange('steps', steps);
	}, [task.steps, onFormChange]);

	// Drag and drop handler
	const onDragEnd = useCallback((result: DropResult) => {
		if (!result.destination) {
			return;
		}

		let steps = [...task.steps.map((step) => ({ ...step }))];
		const [movedStep] = steps.splice(result.source.index, 1);
		steps.splice(result.destination.index, 0, movedStep);

		steps = steps.map((step, index) => ({
			...step,
			ordinal: index,
		}));

		onFormChange('steps', steps);
	}, [task.steps, onFormChange]);

	// Certification toggle disabled check
	const isCertificationToggleDisabled = useCallback(() => {
		if (!formFilters.certifiedTaskId) {
			return false;
		} else {
			return formFilters.certifiedTaskId !== task.id;
		}
	}, [formFilters.certifiedTaskId, task.id]);

	// Link change handlers
	const onLinkChange = useCallback((link: TaskLink, index: number, field: keyof TaskLink, event: React.ChangeEvent<HTMLInputElement>) => {
		let links = task.links.slice();
		const updatedLink = { ...link, [field]: event.target.value };
		links.splice(index, 1, updatedLink);
		onFormChange('links', links);
	}, [task.links, onFormChange]);

	const onFileLinkChange = useCallback((link: TaskLink, index: number, url: string) => {
		const selectedFile = formFilters.subscriptionFiles?.find((m) => m.url === url);
		if (!selectedFile) return;

		let links = task.links.slice();
		const updatedLink = {
			...link,
			name: selectedFile.name,
			url: url,
			customerFileId: selectedFile.id,
		};
		links.splice(index, 1, updatedLink);
		onFormChange('links', links);
	}, [formFilters.subscriptionFiles, task.links, onFormChange]);

	// Same name click handler
	const onSameNameClick = useCallback(() => {
		RouteWithParams(
			Routes.CrossBaselineSearch,
			[
				{
					key: CROSS_BASELINE_SEARCH_URL_PARAMS.name,
					value: task.name.trim(),
				},
			],
			true
		);
	}, [task.name]);

	// Workflow change handler
	const onWorkflowChange = useCallback((workflows: any[]) => {
		// Only update state and call onFormChange if workflows actually changed
		if (!isEqual(workflows, state.selectedWorkflows)) {
			setState((prev) => ({ ...prev, selectedWorkflows: workflows }));
			onFormChange('workflows', workflows);
		}
	}, [onFormChange, state.selectedWorkflows]);

	// Numeric input handler
	const handleNumericInputChange = useCallback((fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		const numericVal = val === '' || isNaN(Number(val)) ? null : Number(val);
		onFormChange(fieldName, numericVal);
	}, [onFormChange]);

	// Submit handler
	const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setState((prev) => ({ ...prev, triedSubmit: true }));

		const ok = validateTargetFields();
		if (!ok) {
			setState((prev) => ({ ...prev, showTargetError: true }));
			formRef.current?.reportValidity();
			return;
		}

		setState((prev) => ({ ...prev, showTargetError: false }));

		if (typeof onSave === 'function') {
			onSave();
		}
	}, [validateTargetFields, onSave]);

	// Effects
	useEffect(() => {
		// Focus on new step when added
		if (task.steps.length > 0) {
			const step = document.getElementById(`stepname${task.steps.length - 1}`);
			step?.focus();
		}
	}, [task.steps.length]);

	useEffect(() => {
		// Clear actionId when eventId changes
		if (task.eventId !== state.eventId) {
			onFormChange('actionId', undefined);
			setState((prev) => ({ ...prev, eventId: task.eventId }));
		}
	}, [task.eventId, state.eventId, onFormChange]);

	useEffect(() => {
		// Clear workflowEmails when actionId changes
		if (task.actionId) {
			onFormChange('workflowEmails', []);
		}
	}, [task.actionId, onFormChange]);

	useEffect(() => {
		// Update selectedWorkflows when task.workflows changes, but only if different (deep compare)
		if (!isEqual(task.workflows || [], state.selectedWorkflows)) {
			setState((prev) => ({ ...prev, selectedWorkflows: task.workflows || [] }));
		}
	}, [task.workflows, state.selectedWorkflows]);

	// Render computed values
	const { targetWeeks, targetDays } = task;
	const hasWeeks = isValidNumber(targetWeeks);
	const hasDays = isValidNumber(targetDays);
	const milestoneLinkText = task.isMilestone ? 'Change to Task' : 'Change to Milestone';
	const milestoneInputText = task.isMilestone ? 'Milestone Task Name' : 'Task Name';
	const disableCertificationToggle = isCertificationToggleDisabled();
	const priorToLabel = formFilters.isProjectWidgetActive ? 'prior to Zero Date' : 'prior to opening';
	const sameNameLabel = task.sameNameCount && task.sameNameCount > 1
		? `There are ${task.sameNameCount} additional active baseline tasks with this same name.`
		: `There is ${task.sameNameCount} additional active baseline task with this same name.`;

	return (
		<div className="baseline-task-form">
			<form
				ref={formRef}
				noValidate
				onSubmit={handleSubmit}
				className={`needs-validation ${!isFormValid || state.triedSubmit ? 'was-validated' : ''}`}
			>
				{task.sameNameCount && task.sameNameCount > 0 ? (
					<div className="alert alert-warning text-center">
						<span>{sameNameLabel}</span>
						<span className="alert-icon ms-2" onClick={onSameNameClick}>
							<Icon name="external-link-alt" />
						</span>
					</div>
				) : null}

				{task.contingentId && (
					<div className="alert alert-secondary contingent-alert">
						<div className="contingent-alert-link">
							This task is a prerequisite of:{' '}
							<a href="#" onClick={() => showRelationshipDrawer(task.contingentId!)}>
								{TruncateText(task.contingentName || '', 20)}
							</a>
						</div>
						<div className="contingent-alert-remove">
							<button type="button" className="remove-dependent btn btn-link" onClick={onSuccessorRemove}>
								Remove dependency
							</button>
						</div>
					</div>
				)}

				{task.prereqTasks && task.prereqTasks.length > 0 && (
					<div className="alert alert-secondary contingent-alert">
						<div>
							This task has the following pre-requisites:
							{task.prereqTasks.map((prereq) => (
								<div className="col-12" key={prereq.id}>
									<span className="me-2 remove" onClick={() => onRemovePrereq(prereq.id)}>
										<Icon name="times" />
									</span>
									<a href="#" onClick={() => showRelationshipDrawer(prereq.id)}>
										{TruncateText(prereq.name, 80)}
									</a>
								</div>
							))}
						</div>
					</div>
				)}

				<div className="row">
					<div className="col-sm-6">
						<div className="row">
							<div className="col-12 milestone text-end">
								<button type="button" className="btn btn-link" onClick={onMakeMilestone}>
									{milestoneLinkText}
								</button>
							</div>
						</div>

						<TextArea
							rows={2}
							name="name"
							label={milestoneInputText}
							placeholder={milestoneInputText}
							value={task.name}
							isRequired={true}
							errorMessage="Name is required"
							onChange={onFormChangeHandler}
						/>

						<TextArea
							rows={2}
							name="additionalInfo"
							placeholder="Additional Info"
							label="Additional Info"
							value={task.additionalInfo}
							onChange={onFormChangeHandler}
						/>

						<div className="mb-3">
							<Toggle
								name="isActive"
								text="Active"
								checked={task.isActive}
								onChange={onToggleChange}
							/>
						</div>

						{!task.isActive && (
							<div>
								<Toggle
									name="isLocationTaskListActive"
									text="Visible in Location Task List"
									checked={task.isLocationTaskListActive || false}
									onChange={onToggleChange}
								/>
								<br />
							</div>
						)}

						<MultiSelectBox
							name="jobTitleId"
							label="Baseline Job Titles"
							options={formFilters.baselineJobTitles ?? []}
							idField="id"
							valueField="name"
							selectedValues={task.jobTitleId.toString()}
							onChange={onDropdownChange}
							isRequired={true}
							errorMessage="Baseline Job Title is required"
							isMulti={false}
						/>

						<MultiSelectBox
							name="departmentId"
							label="Department"
							options={formFilters.departments ?? []}
							idField="id"
							valueField="name"
							selectedValues={task.departmentId.toString()}
							onChange={onDropdownChange}
							isRequired={true}
							errorMessage="Department is required"
							isMulti={false}
						/>

						<MultiSelectBox
							name="communityId"
							label="Community"
							options={formFilters.communities ?? []}
							idField="id"
							valueField="name"
							selectedValues={task.communityId.toString()}
							onChange={onDropdownChange}
							isRequired={true}
							errorMessage="Community is required"
							isMulti={false}
						/>

						{state.showMore ? (
							<div>
								<MultiSelectBox
									name="taskTagIds"
									label="Task Tags"
									options={formFilters.taskTags ?? []}
									idField="id"
									valueField="name"
									selectedValues={Array.isArray(task.taskTagIds) ? task.taskTagIds.join(',') : (task.taskTagIds || '')}
									onChange={onDropdownChange}
									isMulti={true}
								/>

								<MultiSelectBox
									name="phaseId"
									label="Phase"
									options={formFilters.phases ?? []}
									idField="id"
									valueField="name"
									selectedValues={task.phaseId?.toString() || ''}
									onChange={onDropdownChange}
									isMulti={false}
								/>

								<div className="mt-2">
									{task.links.map((link, index) => (
										<table className="links" key={link.ordinal}>
											<tbody>
												{link.isFile ? (
													<tr>
														<td>
															<MultiSelectBox
																name="linkFile"
																label="Files"
																options={formFilters.subscriptionFiles ?? []}
																idField="url"
																valueField="name"
																selectedValues={link.url || ''}
																onChange={(e) => onFileLinkChange(link, index, e as string)}
																isMulti={false}
															/>
														</td>
													</tr>
												) : (
													<tr>
														<td>
															<InputText
																name="linkUrl"
																label="Link URL"
																type="text"
																placeholder="Link URL"
																value={link.url}
																onChange={(e) => onLinkChange(link, index, 'url', e)}
																maxLength={500}
															/>
														</td>
														<td className="td-spacer"></td>
														<td>
															<InputText
																name="linkName"
																label="Link Abbreviation"
																type="text"
																placeholder="Link Abbreviation"
																value={link.name}
																onChange={(e) => onLinkChange(link, index, 'name', e)}
																isRequired={link.url !== ''}
																errorMessage="Abbreviation Required"
																maxLength={50}
															/>
														</td>
													</tr>
												)}
											</tbody>
										</table>
									))}

									{task.links.length < MAX_NUM_TASK_LINKS && (
										<div className="link-buttons">
											<button type="button" className="btn btn-link remove-mt" onClick={addLink}>
												Add Link
											</button>
											{formFilters.subscriptionFiles && formFilters.subscriptionFiles.length > 0 && (
												<Fragment>
													<span>|</span>
													<button type="button" className="btn btn-link remove-mt ms-2" onClick={addFileLink}>
														Add Link from Baseline Files
													</button>
												</Fragment>
											)}
										</div>
									)}
								</div>

								<div className="mt-2">
									<table>
										<tbody>
											<tr>
												<td>
													<InputText
														name="contactName"
														label="Contact Name"
														type="text"
														placeholder="Contact Name"
														value={task.contactName}
														onChange={onFormChangeHandler}
													/>
												</td>
												<td className="td-spacer"></td>
												<td>
													<InputText
														name="contactEmail"
														label="Contact Email"
														type="email"
														placeholder="Contact Email"
														value={task.contactEmail}
														onChange={onFormChangeHandler}
													/>
												</td>
											</tr>
										</tbody>
									</table>
								</div>

								<div className="mb-3">
									{formFilters.isProjectWidgetActive && (
										<Toggle
											name="includeCertificationCheckbox"
											text="Include Certification Checkbox"
											checked={task.includeCertificationCheckbox || false}
											onChange={onToggleChange}
											isDisabled={disableCertificationToggle}
										/>
									)}

									{task.showStaticDates === false && formFilters.isProjectWidgetActive === false && (
										<Toggle
											name="isLocationTaskDateAdjustable"
											text="Adjust location dates if Opening Date changes"
											checked={task.isLocationTaskDateAdjustable || false}
											onChange={onToggleChange}
											isDisabled={false}
										/>
									)}
								</div>

								<WorkflowSelector
									workflows={formFilters.workflows ?? []}
									selectedWorkflows={state.selectedWorkflows}
									onChange={onWorkflowChange}
									isDisabled={!task.isBaselineTaskValidationRulesActive}
								/>

								<button type="button" className="btn btn-link" onClick={showMore}>
									See less
								</button>
							</div>
						) : (
							<button type="button" className="btn btn-link remove-mt" onClick={showMore}>
								Show more
							</button>
						)}
					</div>

					<div className="col-sm-6">
						{task.id !== 0 && formFilters.isTaskDependenciesActive && (
							<div data-testid="dependencies-section">
								{!task.contingentId && (
									<Fragment>
										{state.markAsDependent ? (
											<div className="row">
												<div className="col-12">
													<button type="button" className="btn btn-link" onClick={onMarkAsDependent}>
														Mark as Dependent
													</button>
												</div>
												<div className="col-12">
													<MultiSelectBox
														name="contingentId"
														label="Successor"
														options={taskSearchResults ?? []}
														idField="id"
														valueField="name"
														selectedValues=""
														onChange={onSuccessorAdd}
														onInputKeyDown={onTaskSearchKeyDown}
														onKeyDown={onTaskSearchKeyDown}
														isMulti={false}
														placeholder="Please start typing the task name and press enter"
													/>
												</div>
											</div>
										) : (
											<div className="row">
												<div className="col-12">
													<button
														name="markAsDependent"
														type="button"
														className="btn btn-link"
														onClick={onMarkAsDependent}
													>
														Mark as Dependent
													</button>
												</div>
											</div>
										)}
									</Fragment>
								)}

								{state.linkAsPrereq ? (
									<div className="row">
										<div className="col-12">
											<button type="button" className="btn btn-link" onClick={onLinkAsPrereq}>
												Link a Pre-Requisite Task
											</button>
										</div>
										<div className="col-12">
											<MultiSelectBox
												name="prereqTasks"
												label="Pre-Req"
												options={taskPrereqSearchResults ?? []}
												idField="id"
												valueField="name"
												selectedValues=""
												onChange={onPrereqChange}
												onInputKeyDown={onPrereqSearchKeyDown}
												onKeyDown={onPrereqSearchKeyDown}
												isMulti={false}
												placeholder="Please start typing the task name and press enter"
											/>
										</div>
									</div>
								) : (
									<div className="row">
										<div className="col-12">
											<button type="button" className="btn btn-link" onClick={onLinkAsPrereq}>
												Link a Pre-Requisite Task
											</button>
										</div>
									</div>
								)}
							</div>
						)}

						{showDateError && (
							<div className="alert alert-danger" role="alert">
								Target date must be after Start Date
							</div>
						)}

						{task.showStaticDates ? (
							<div>
								{formFilters.isStartDateActive && (
									<div data-testid="start-date-section">
										<div className="row">
											<div className="col-12">
												<label>Start Date {priorToLabel}</label>
											</div>
										</div>
										<div className="row">
											<div className="col-12">
												<DatePicker
													value={task.startStaticDate || ''}
													name="startStaticDate"
													label=""
													onChange={onDatePickerChange}
												/>
											</div>
										</div>
									</div>
								)}

								<div className="row">
									<div className="col-12">
										<label>Target Date {priorToLabel}</label>
									</div>
								</div>
								<div className="row">
									<div className="col-12">
										<DatePicker
											value={task.targetStaticDate || ''}
											name="targetStaticDate"
											label=""
											onChange={onDatePickerChange}
											isRequired={true}
											errorMessage="Target Date Required"
										/>
									</div>
								</div>
								<div className="row">
									<div className="col-12">
										<button type="button" className="btn btn-link remove-mt" onClick={onAddStaticDates}>
											Switch to estimated dates
										</button>
									</div>
								</div>
							</div>
						) : (
							<div>
								{formFilters.isStartDateActive && (
									<div data-testid="start-date-section">
										<div className="row">
											<div className="col-12 mb-2">
												<label>Start Date - Weeks/Days {priorToLabel}</label>
											</div>
										</div>
										<table>
											<tbody>
												<tr>
													<td>
														<InputText
															name="startWeeks"
															label="Weeks"
															type="number"
															placeholder="Weeks"
															value={task.startWeeks?.toString() ?? ''}
															onChange={handleNumericInputChange('startWeeks')}
														/>
													</td>
													<td className="td-spacer"></td>
													<td>
														<InputText
															name="startDays"
															label="Days"
															type="number"
															placeholder="Days"
															value={task.startDays?.toString() ?? ''}
															onChange={handleNumericInputChange('startDays')}
														/>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								)}

								<div className="row">
									<div className="col-12 mb-2">
										<label>Target Date - Weeks/Days {priorToLabel}</label>
									</div>
								</div>
								<table>
									<tbody>
										<tr>
											<td>
												<InputText
													name="targetWeeks"
													label="Weeks"
													type="number"
													placeholder="Weeks"
													value={task.targetWeeks?.toString() ?? ''}
													onChange={onFormChangeHandler}
												/>
											</td>
											<td className="td-spacer"></td>
											<td>
												<InputText
													name="targetDays"
													label="Days"
													type="number"
													placeholder="Days"
													value={task.targetDays?.toString() ?? ''}
													onChange={onFormChangeHandler}
												/>
											</td>
										</tr>
									</tbody>
								</table>

								{(state.triedSubmit || !isFormValid) && !hasWeeks && !hasDays && (
									<div className="invalid-feedback d-block">Please fill in at least Weeks or Days.</div>
								)}

								<div className="row">
									<div className="col-12">
										<button type="button" className="btn btn-link remove-mt" onClick={onAddStaticDates}>
											Switch to static dates
										</button>
									</div>
								</div>
							</div>
						)}

						{formFilters.isTaskEffortActive && (
							<div data-testid="effort-section" className="row">
								<div className="col-12 mt-3">
									<InputText
										name="anticipatedEffort"
										label="Anticipated Effort"
										type="text"
										placeholder="Anticipated Effort"
										value={task.anticipatedEffort}
										onChange={onFormChangeHandler}
									/>
								</div>
							</div>
						)}

						{formFilters.isTaskStepsActive && (
							<div data-testid="steps-section">
								<div className="row">
									<div className="col-12 mt-2 remove-mb">
										<label>Task Steps</label>
									</div>
								</div>

								<DragDropContext onDragEnd={onDragEnd}>
									{/* @ts-ignore - Known React version compatibility issue with @hello-pangea/dnd */}
									<Droppable droppableId="droppable">
										{(provided, snapshot) => (
											<div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
												{task.steps.map((step, index) => (
													<Draggable key={step.ordinal} draggableId={step.ordinal.toString()} index={index}>
														{(provided, snapshot) => (
															<div
																className="task-step row"
																ref={provided.innerRef}
																{...provided.draggableProps}
																{...provided.dragHandleProps}
																style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
															>
																<div className="col-1">
																	<div className="task-step-bars">
																		<Icon name="bars" />
																	</div>
																</div>
																<div className="col-10">
																	<InputText
																		name={`stepname${index}`}
																		label="Name"
																		type="text"
																		placeholder="Name"
																		value={step.name}
																		onChange={(e) => onUpdateStepName(step, e)}
																		isRequired={true}
																		errorMessage="Name Required"
																		maxLength={200}
																	/>
																</div>
																<div className="col-1">
																	<div
																		className="task-step-remove text-center"
																		onClick={() => onRemoveStep(step)}
																	>
																		<Icon name="trash-alt" />
																	</div>
																</div>
															</div>
														)}
													</Draggable>
												))}
												{provided.placeholder as React.ReactNode}
											</div>
										)}
									</Droppable>
								</DragDropContext>

								{task.steps.length < MAX_TASK_STEP_COUNT && (
									<div className="row">
										<div className="col-12 remove-mt">
											<button name="addStep" type="button" className="btn btn-link remove-mt" onClick={onAddStep}>
												Add a Step
											</button>
										</div>
									</div>
								)}
							</div>
						)}

						{formFilters.isTaskFeedbackActive && (
							<div data-testid="feedback-section">
							</div>
						)}
					</div>
				</div>

				{task.id > 0 && (
					<div className="row mt-3">
						<div className="col-md-6">
							<InputText
								name="createdByName"
								isDisabled={true}
								label="Created By"
								type="text"
								value={task.createdByName}
							/>
							<InputText
								name="createdDate"
								isDisabled={true}
								label="Created Date"
								type="text"
								value={task.dateCreated}
							/>
						</div>
						<div className="col-md-6">
							<InputText
								name="modifiedByName"
								isDisabled={true}
								label="Modified By"
								type="text"
								value={task.modifiedByName}
							/>
							<InputText
								name="modifiedDate"
								isDisabled={true}
								label="Modified Date"
								type="text"
								value={task.dateModified}
							/>
						</div>
					</div>
				)}
			</form>

			{state.isRelationshipDrawerOpen && (
				<BaselineTaskDrawer
					subscriptionId={subscriptionId}
					taskId={state.relationshipBaselineTaskId!}
					baselineId={task.baselineId}
					onComplete={onRelationshipDrawerComplete}
					onCancel={onRelationshipDrawerComplete}
					formFilters={formFilters}
					isMobile={isMobile}
				/>
			)}
		</div>
	);
};

// Main Export Component with Error Boundary
const BaselineTaskForm: React.FC<BaselineTaskFormProps> = (props) => {
	return (
		<ErrorBoundary
			FallbackComponent={ErrorFallback}
			onReset={() => window.location.reload()}
		>
			<BaselineTaskFormContent {...props} />
		</ErrorBoundary>
	);
};

export default BaselineTaskForm;